'use client';

import {
  forwardRef,
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
} from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';

import { cn } from '../utils/cn';

export type DrawerSide = 'bottom' | 'right' | 'left' | 'top';

export const Drawer = ({
  shouldScaleBackground,
  direction = 'bottom',
  ...props
}: ComponentProps<typeof DrawerPrimitive.Root>) => (
  // Background-scaling is a bottom-sheet affordance; disable it for side
  // drawers (right/left) unless the caller explicitly opts in.
  <DrawerPrimitive.Root
    direction={direction}
    shouldScaleBackground={shouldScaleBackground ?? direction === 'bottom'}
    {...props}
  />
);
Drawer.displayName = 'Drawer';

export const DrawerTrigger = DrawerPrimitive.Trigger;
export const DrawerPortal = DrawerPrimitive.Portal;
export const DrawerClose = DrawerPrimitive.Close;

export const DrawerOverlay = forwardRef<
  ElementRef<typeof DrawerPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-50 bg-black/70', className)}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const drawerSideClasses: Record<DrawerSide, string> = {
  bottom: 'inset-x-0 bottom-0 mt-24 h-auto rounded-t-[10px] border',
  top: 'inset-x-0 top-0 mb-24 h-auto rounded-b-[10px] border',
  right: 'inset-y-0 right-0 h-full w-full border-l sm:w-[480px]',
  left: 'inset-y-0 left-0 h-full w-full border-r sm:w-[480px]',
};

export const DrawerContent = forwardRef<
  ElementRef<typeof DrawerPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & { side?: DrawerSide }
>(({ className, children, side = 'bottom', ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn('bg-background fixed z-50 flex flex-col', drawerSideClasses[side], className)}
      {...props}
    >
      {/* Grabber only makes sense on the bottom sheet. */}
      {side === 'bottom' && <div className="bg-muted mx-auto mt-4 h-2 w-[100px] rounded-full" />}
      {children}
    </DrawerPrimitive.Content>
  </DrawerPortal>
));
DrawerContent.displayName = 'DrawerContent';

export const DrawerHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)} {...props} />
);

export const DrawerFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />
);

export const DrawerTitle = forwardRef<
  ElementRef<typeof DrawerPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn('font-heading text-lg leading-none font-semibold tracking-tight', className)}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

export const DrawerDescription = forwardRef<
  ElementRef<typeof DrawerPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-muted-foreground text-sm', className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;
